import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropzone", "preview", "progress"]
  
  connect() {
    console.log('File upload controller connected')
    this.setupDropzone()
  }
  
  setupDropzone() {
    const dropzone = this.dropzoneTarget
    
    // Drag and drop events
    dropzone.addEventListener('dragover', this.handleDragOver.bind(this))
    dropzone.addEventListener('dragleave', this.handleDragLeave.bind(this))
    dropzone.addEventListener('drop', this.handleDrop.bind(this))
    
    // Click to select files
    dropzone.addEventListener('click', () => {
      this.inputTarget.click()
    })
    
    // File input change
    this.inputTarget.addEventListener('change', this.handleFileSelect.bind(this))
  }
  
  handleDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900')
  }
  
  handleDragLeave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900')
  }
  
  handleDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900')
    
    const files = event.dataTransfer.files
    console.log('Files dropped:', files.length)
    this.processFiles(files)
  }
  
  handleFileSelect(event) {
    const files = event.target.files
    this.processFiles(files)
  }
  
  processFiles(files) {
    const fileArray = Array.from(files)
    
    // Validate files
    const validFiles = fileArray.filter(file => {
      if (file.size > 5 * 1024 * 1024 * 1024) { // 5GB
        this.showError(`File "${file.name}" exceeds 5GB limit`)
        return false
      }
      return true
    })
    
    if (validFiles.length === 0) return
    
    // Update input with valid files
    this.updateFileInput(validFiles)
    
    // Show preview
    this.showPreview(validFiles)
  }
  
  updateFileInput(files) {
    const dt = new DataTransfer()
    files.forEach(file => dt.items.add(file))
    this.inputTarget.files = dt.files
  }
  
  showPreview(files) {
    const preview = this.previewTarget
    preview.innerHTML = ''
    
    files.forEach(file => {
      const fileItem = document.createElement('div')
      fileItem.className = 'flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md'
      
      fileItem.innerHTML = `
        <div class="flex items-center space-x-3">
          <i class="fas fa-file text-gray-400"></i>
          <div>
            <div class="text-sm font-medium text-gray-900 dark:text-white">${file.name}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">${this.formatFileSize(file.size)}</div>
          </div>
        </div>
        <button type="button" class="text-red-600 hover:text-red-800" data-action="click->file-upload#removeFile" data-filename="${file.name}">
          <i class="fas fa-times"></i>
        </button>
      `
      
      preview.appendChild(fileItem)
    })
    
    preview.classList.remove('hidden')
  }
  
  removeFile(event) {
    const filename = event.currentTarget.dataset.filename
    const currentFiles = Array.from(this.inputTarget.files)
    const filteredFiles = currentFiles.filter(file => file.name !== filename)
    
    if (filteredFiles.length === 0) {
      this.inputTarget.value = ''
      this.previewTarget.classList.add('hidden')
    } else {
      this.updateFileInput(filteredFiles)
      this.showPreview(filteredFiles)
    }
  }
  
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
  
  showError(message) {
    // Create a temporary error notification
    const notification = document.createElement('div')
    notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-md shadow-lg z-50'
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }
}